import { ReactNode, useCallback, useMemo } from 'react';

import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { BiomassReportSummary } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { DataRow, DataRowDivider } from '@/components/dataRow';
import { responsive, round } from '@/utils';

type BiomassContentProps = {
  data: BiomassReportSummary | undefined;
  hasSensor: boolean;
  isError: boolean;
  isFetched: boolean;
  isLoading: boolean;
};

export const BiomassContent = ({
  data,
  hasSensor,
  isError,
  isFetched,
  isLoading,
}: BiomassContentProps) => {
  const { t } = useTranslation(['orbitOne']);

  const { biomass, coefficientOfVariation, kFactor, specificGrowthRate, standardDeviation } =
    data ?? {};

  const dataRowIsFetched = !hasSensor || isFetched;

  const formattedBiomassWithDeviation = useMemo(() => {
    // 1. Early return if biomass is missing.
    // Note: Using `== null` safely checks for BOTH null and undefined,
    // while still allowing a valid value of `0` to pass through.
    if (biomass == null) return undefined;

    const formattedBiomass = round(biomass, 2);

    // 2. Return with or without standard deviation
    return standardDeviation != null ? (
      <FlexAligner alignment='bottom-left' gap={responsive.spacing.xsmall}>
        <Typography type={'heading_medium'}>{formattedBiomass}</Typography>
        <Typography type={'heading_small'}>{`±${round(standardDeviation, 2)} kg`}</Typography>
      </FlexAligner>
    ) : (
      formattedBiomass
    );
  }, [biomass, standardDeviation]);

  const formattedKFactor = useMemo(() => {
    if (kFactor === undefined || kFactor === null) return undefined;
    return `${round(kFactor, 2)}`;
  }, [kFactor]);

  const formattedSpecificGrowthRate = useMemo(
    () =>
      specificGrowthRate !== undefined && specificGrowthRate !== null
        ? `${round(specificGrowthRate * 100, 2)}%`
        : undefined,
    [specificGrowthRate]
  );

  const formattedCoefficientOfVariation = useMemo(
    () =>
      coefficientOfVariation !== undefined && coefficientOfVariation !== null
        ? `${round(coefficientOfVariation * 100)}%`
        : undefined,
    [coefficientOfVariation]
  );
  const getDataRow = useCallback(
    ({ label, value }: { label: string | ReactNode; value: string | ReactNode }) => (
      <DataRow
        isError={isError}
        isFetched={dataRowIsFetched}
        isLoading={isLoading}
        label={label}
        value={value}
      />
    ),
    [dataRowIsFetched, isError, isLoading]
  );

  return (
    <>
      {getDataRow({
        label: t('orbitOne:biomass.labels.weight'),
        value: formattedBiomassWithDeviation,
      })}
      <DataRowDivider />
      {getDataRow({
        label: t('orbitOne:biomass.labels.specificGrowthRate'),
        value: formattedSpecificGrowthRate,
      })}
      <DataRowDivider />
      {getDataRow({
        label: t('orbitOne:biomass.labels.coefficientOfVariation'),
        value: formattedCoefficientOfVariation,
      })}
      <DataRowDivider />
      {getDataRow({
        label: t('orbitOne:biomass.labels.kFactor'),
        value: formattedKFactor,
      })}
    </>
  );
};
